// ==============================================================================
// UTILITÁRIOS DE AGENDAMENTO, ICAL (.ICS) E ALARMES (GOOGLE CALENDAR & APPLE IOS)
// Configura avisos inteligentes: Alarme às 08:00 da manhã do dia + 45 min antes
// ==============================================================================

export interface CalendarEventParams {
  title: string;
  description: string;
  location?: string;
  scheduledDate: string; // YYYY-MM-DD
  scheduledTime: string; // HH:MM
  durationMinutes: number;
  providerName: string;
  clientName?: string;
}

/**
 * Formata data e hora para o padrão UTC iCalendar (YYYYMMDDTHHMMSSZ)
 */
function formatIcsDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Formata data e hora no fuso horário local para uso com TZID (YYYYMMDDTHHMMSS)
 * RFC 5545 exige horário civil local quando especificado TZID (sem Z final).
 */
function formatIcsLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${y}${m}${d}T${hh}${mm}${ss}`;
}

/**
 * Calcula o offset em minutos para disparar o alarme às 08:00 da manhã do mesmo dia.
 * Ex: Se o evento é às 15:30 (930min desde 00:00), 08:00 é 480min. Offset = 450 min antes.
 */
function calculateOffsetMinutesTo8AM(scheduledTime: string): number {
  const [hStr, mStr] = scheduledTime.split(':');
  const eventHour = parseInt(hStr, 10) || 14;
  const eventMinute = parseInt(mStr, 10) || 0;

  const eventMinutesFromMidnight = eventHour * 60 + eventMinute;
  const target8AMMinutes = 8 * 60; // 480 min (08:00 da manhã)

  if (eventMinutesFromMidnight > target8AMMinutes) {
    return eventMinutesFromMidnight - target8AMMinutes;
  }
  // Se o agendamento for às 08:00 ou antes, dispara 30 minutos antes
  return 30;
}

/**
 * Gera o arquivo iCalendar (.ics) com alarme prioritário às 08:00 da manhã + 45 min antes
 */
export function generateIcsContent({
  title,
  description,
  location = '',
  scheduledDate,
  scheduledTime,
  durationMinutes,
  providerName,
  clientName
}: CalendarEventParams): string {
  const [year, month, day] = scheduledDate.split('-').map(Number);
  const [hour, minute] = scheduledTime.split(':').map(Number);

  const startDate = new Date(year, month - 1, day, hour, minute);
  const endDate = new Date(startDate.getTime() + (durationMinutes || 60) * 60000);
  const now = new Date();

  const offsetMinutesTo8AM = calculateOffsetMinutesTo8AM(scheduledTime);

  const uid = 'rmax_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9) + '@resolvmax.com.br';

  // RFC 5545 iCalendar standard com VTIMEZONE estrito para América/São Paulo
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//R-MAX Precificacao & ERP//PT-BR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:' + title,
    'X-WR-TIMEZONE:America/Sao_Paulo',
    'BEGIN:VTIMEZONE',
    'TZID:America/Sao_Paulo',
    'X-LIC-LOCATION:America/Sao_Paulo',
    'BEGIN:STANDARD',
    'TZOFFSETFROM:-0300',
    'TZOFFSETTO:-0300',
    'TZNAME:BRT',
    'DTSTART:19700101T000000',
    'END:STANDARD',
    'END:VTIMEZONE',
    'BEGIN:VEVENT',
    'UID:' + uid,
    'DTSTAMP:' + formatIcsDate(now),
    'DTSTART;TZID=America/Sao_Paulo:' + formatIcsLocalDate(startDate),
    'DTEND;TZID=America/Sao_Paulo:' + formatIcsLocalDate(endDate),
    'SUMMARY:' + title,
    'DESCRIPTION:' + description.replace(/\n/g, '\\n'),
    'LOCATION:' + location,
    'ORGANIZER;CN=' + providerName + ':mailto:rmax@resolvmax.com.br',
    'STATUS:CONFIRMED',

    // ALARME 1: Às 08:00 da manhã do dia do agendamento
    'BEGIN:VALARM',
    'TRIGGER:-PT' + offsetMinutesTo8AM + 'M',
    'ACTION:DISPLAY',
    'DESCRIPTION:⏰ Lembrete do Dia (08:00): ' + title,
    'END:VALARM',

    // ALARME 2: 45 minutos antes do horário (para deslocamento)
    'BEGIN:VALARM',
    'TRIGGER:-PT45M',
    'ACTION:DISPLAY',
    'DESCRIPTION:🚗 Seu atendimento começa em 45 minutos: ' + title,
    'END:VALARM',

    'END:VEVENT',
    'END:VCALENDAR'
  ];

  return icsLines.join('\r\n');
}

/**
 * Dispara o download automático do arquivo .ics (compatível com Apple Calendar / iOS, Android e Outlook)
 */
export function downloadIcsFile(params: CalendarEventParams, fileName?: string): void {
  const content = generateIcsContent(params);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName || 'agendamento-rmax.ics');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Gera URL do Google Calendar Web
 */
export function generateGoogleCalendarUrl({
  title,
  description,
  location = '',
  scheduledDate,
  scheduledTime,
  durationMinutes
}: CalendarEventParams): string {
  const [year, month, day] = scheduledDate.split('-').map(Number);
  const [hour, minute] = scheduledTime.split(':').map(Number);

  const startDate = new Date(year, month - 1, day, hour, minute);
  const endDate = new Date(startDate.getTime() + (durationMinutes || 60) * 60000);

  const formatGCal = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, '');

  return (
    'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' +
    encodeURIComponent(title) +
    '&dates=' +
    formatGCal(startDate) +
    '/' +
    formatGCal(endDate) +
    '&details=' +
    encodeURIComponent(description) +
    '&location=' +
    encodeURIComponent(location)
  );
}

export const TIMEZONE_BRT = 'America/Sao_Paulo';

/**
 * Retorna a data no formato YYYY-MM-DD considerando o fuso horário oficial do Brasil (America/Sao_Paulo / UTC-3).
 * Garante que a virada de dia civil ocorra rigorosamente à meia-noite (00:00:00),
 * evitando a discrepância do toISOString() que avança o dia indevidamente às 21:00 (UTC-3).
 */
export function getLocalDateString(d: Date | string | number = new Date(), timeZone = TIMEZONE_BRT): string {
  if (typeof d === 'string') {
    const trimmed = d.trim();
    // Se for uma data civil pura no formato YYYY-MM-DD, preserva diretamente sem passar pelo construtor UTC do Date
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
  }

  try {
    const dateObj = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
    if (isNaN(dateObj.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(dateObj);
  } catch {
    const dateObj = typeof d === 'string' || typeof d === 'number' ? new Date(d) : d;
    if (isNaN(dateObj.getTime())) return '';
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

/**
 * Converte string 'YYYY-MM-DD' para objeto Date seguro ao meio-dia (12:00:00 local),
 * blindando o cálculo contra shifts de timezone (ex: new Date('YYYY-MM-DD') que vira 21:00 do dia anterior em UTC-3).
 */
export function parseCivilDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  const clean = dateStr.split('T')[0];
  const [year, month, day] = clean.split('-').map(Number);
  if (!year || !month || !day) return new Date(dateStr);
  return new Date(year, month - 1, day, 12, 0, 0);
}

/**
 * Formata diretamente uma string 'YYYY-MM-DD' no padrão brasileiro 'DD/MM/YYYY',
 * sem intermediar com Date.toLocaleDateString, imune a recuo de data no fuso UTC-3.
 */
export function formatCivilDateBR(dateStr?: string): string {
  if (!dateStr) return '';
  const clean = dateStr.split('T')[0];
  const parts = clean.split('-');
  if (parts.length !== 3) {
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  }
  const [year, month, day] = parts;
  return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
}

/**
 * Calcula a diferença em dias inteiros entre duas datas civis ('YYYY-MM-DD').
 * Retorna:
 *  0 = mesmo dia (vence hoje)
 * >0 = dias no futuro
 * <0 = dias no passado (atrasado)
 */
export function diffCivilDays(targetDateStr: string, referenceDateStr?: string): number {
  const refIso = referenceDateStr ? referenceDateStr.split('T')[0] : getLocalDateString();
  const targetIso = targetDateStr ? targetDateStr.split('T')[0] : '';
  if (!targetIso || !refIso) return 0;

  const target = parseCivilDate(targetIso);
  const ref = parseCivilDate(refIso);
  const diffMs = target.getTime() - ref.getTime();
  return Math.round(diffMs / (1000 * 60 * 60 * 24));
}

